import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'How to Write a Fundraising Page Bio That Gets People to Give (With Examples)';
const DESCRIPTION =
    "Your fundraising page bio does more work than any single support letter — it's what a donor reads right before deciding to give. A practical structure, two before/after examples, and the mistakes that quietly cost you donations.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover3.jpg',
            datePublished: '2026-07-31',
            dateModified: '2026-07-31',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/how-to-write-a-fundraising-page-bio-that-gets-donations',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'How long should a fundraising page bio be?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Long enough to answer who you are, what you're doing, and why it matters — usually 150 to 300 words. A first-time visitor deciding whether to give rarely reads a long biography, so the goal is clarity in a few short paragraphs, not a complete life story.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "What's the biggest mistake missionaries make in their bio?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Leading with a resume — where you grew up, where you went to school, every role you've held — before ever saying what you're actually doing now and what a gift accomplishes. A donor skims for relevance first and credentials second.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should a fundraising bio ask directly for money?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes, in one clear closing line. A bio that only describes the work and never states what a gift is for leaves the donor to guess the ask, which quietly lowers conversion. Close with a specific, concrete sentence about what their support funds.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Should I write my bio in first person?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. First person ('I moved to...' rather than 'Jussi moved to...') reads as a personal update from someone the donor knows, which is closer to how most gifts are actually decided — relationship first, cause second.",
                    },
                },
            ],
        },
    ],
};

export default function HowToWriteAFundraisingPageBio() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/how-to-write-a-fundraising-page-bio-that-gets-donations' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-fundraising-bio-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover3.jpg" alt="A missionary writing their fundraising page bio on a laptop" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>A support letter goes out once and gets read once. A fundraising page bio gets read every single time someone new lands on your page — a friend-of-a-friend, a church member forwarded the link, a donor deciding whether to increase their gift. It's the single piece of writing doing the most repeat work in your entire support-raising effort, and most missionaries write it last, in ten rushed minutes, after setting up everything else.</p>

                    <h2>What a bio actually needs to do</h2>

                    <p>A visitor reading your bio is silently answering three questions in order: who is this person, what are they actually doing, and why does it matter that I give. Most bios answer the first question at length and the other two barely at all — a mistake, since the first question is the one a donor cares about least on a first visit.</p>

                    <h2>A structure that works</h2>

                    <ol>
                        <li><strong>Open with the work, not your history.</strong> One sentence on what you're doing right now and where — not where you were born or where you went to school.</li>
                        <li><strong>Give one concrete detail.</strong> A specific place, a specific group you serve, a specific problem you're addressing. Specificity is what makes a bio feel real instead of generic.</li>
                        <li><strong>Say why it matters, briefly.</strong> One or two sentences connecting the work to a bigger why — without turning into a sermon.</li>
                        <li><strong>Close with a direct, specific ask.</strong> What a gift actually funds, stated plainly.</li>
                    </ol>

                    <p>That's the whole shape. Credentials, extended personal history, and a full ministry philosophy can live on a separate page or in your first newsletter — they're not what earns the first gift.</p>

                    <h2>Before and after: two real patterns</h2>

                    <p><strong>Before:</strong> "Hi, I'm Sarah! I grew up in Ohio and always loved helping people. I went to Bible college and always felt called to missions. After graduating I joined a mission organization and have been serving for two years now. I love what I do and I'm so thankful for anyone who can support me!"</p>

                    <p><strong>After:</strong> "I run a weekly literacy program for 40 kids in a refugee community outside Nairobi — many of whom had never held a book before this year. Your monthly gift covers books, a teacher's stipend, and the room we meet in. $30/month funds one child's full year in the program."</p>

                    <p>The second version answers what, where, and why does it matter in three sentences, and closes with a concrete number a donor can picture funding. Nothing about Sarah's background was removed from the page — it just moved to a separate "my story" section instead of leading the pitch.</p>

                    <h2>Common mistakes that quietly cost donations</h2>

                    <ul>
                        <li><strong>Leading with a resume.</strong> Where you're from and where you studied matters far less on a first visit than what you're doing right now.</li>
                        <li><strong>Staying vague on purpose.</strong> "Serving the Lord in East Asia" tells a donor nothing they can picture funding. Name the actual work.</li>
                        <li><strong>Never stating the ask.</strong> A bio that only describes the mission and never says what a gift is for leaves the donor to guess — and most won't fill in the blank themselves.</li>
                        <li><strong>Writing in third person.</strong> It reads like a press release instead of a personal update from someone the donor knows.</li>
                        <li><strong>Making it too long.</strong> A wall of text gets skimmed, not read. Keep the page bio to 150–300 words and put the rest in an "our story" link or your newsletter.</li>
                    </ul>

                    <h2>Update it, don't just set it once</h2>

                    <p>A bio written before you left for the field goes stale fast — "preparing to move to" should become "living in" within weeks of arrival, and a program you launched deserves a mention the same month it starts. Treat your bio as a living piece of your page, not a one-time setup task, the same way you'd treat a newsletter update.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>How long should a fundraising bio be?</strong><br />
                    Around 150–300 words — enough to answer who, what, and why, without becoming a full life story.</p>

                    <p><strong>What's the biggest mistake to avoid?</strong><br />
                    Leading with your resume and history instead of what you're actually doing now and why it matters.</p>

                    <p><strong>Should it directly ask for money?</strong><br />
                    Yes — close with one specific, concrete line about what a gift funds.</p>

                    <p><strong>First person or third person?</strong><br />
                    First person. It reads as a personal update, which is closer to how most giving decisions actually get made.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-write-a-missionary-support-letter">How to Write a Missionary Support Letter That Actually Gets You Funded</a></li>
                        <li><a href="/resources/free-missionary-newsletter-template">Free Missionary Newsletter Template + 3 Real Examples Teardown</a></li>
                        <li><a href="/resources/how-to-write-a-prayer-letter-people-actually-read">How to Write a Prayer Letter People Actually Read</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We built this guide after watching too many strong support-raising efforts get undercut by a bio that was written in ten rushed minutes and never revisited.</p>
                    <p><small><em>Last updated: 2026-07-31.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/how-to-write-a-fundraising-page-bio-that-gets-donations" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
